// The session controller (FR-018 to FR-027, FR-034, FR-036; data-model §1–§3, §7; research R2–R8).
// It fills 001's ports: the restorer, the credential provider and onUnauthenticated (renew once,
// shared). Every sign-in method ends in completeSignIn. Credentials: the access token lives only
// here (never in store state); the refresh credential is the Android keystore entry or the web
// cookie, with a web-only session hint saying a cookie may exist. The return address lives in the
// URL (/welcome?returnTo=), kept by 001's route guard; the controller never stores it.
import type { AppError } from "@/api/errors";
import { isAppError } from "@/api/errors";
import type { Api } from "@/api/types";
import type { AuthApi } from "@/auth/authApi";
import { otherClientMessage } from "@/auth/messages";
import type { PendingJoin } from "@/join/pendingJoin";

import { servedFromAuth, toSignedInSession } from "./mapAuthSession";
import { resolvePostSignIn } from "./postSignIn";
import { applyRoles, chooseActiveRole } from "./roles";
import { servedRoles } from "./store";
import type {
  E2eSeed,
  Platform,
  ServedRole,
  SessionStore,
  SignedOutReason,
  SignInPayload,
  SignOutCause,
} from "./types";

type AuthSession = Api["AuthSession"];

export type ControllerStorage = {
  refreshCredential: {
    read(): Promise<string | null>;
    write(token: string): Promise<void>;
    clear(): Promise<void>;
  };
  sessionHint: {
    present(): Promise<boolean>;
    set(): Promise<void>;
    clear(): Promise<void>;
  };
  activeRolePreference: {
    read(): Promise<ServedRole | null>;
    write(role: ServedRole): Promise<void>;
    clear(): Promise<void>;
  };
};

export type ControllerDeps = {
  /** The store is created first (its ports call the controller), so it is read lazily. */
  store: () => SessionStore;
  api: Pick<AuthApi, "refreshSession" | "signOut">;
  platform: Platform;
  storage: ControllerStorage;
  /** Read only: no step of this feature writes or clears it, except the user's Log out (FR-036). */
  pendingJoin: {
    read(now?: number): Promise<PendingJoin | null>;
    clear(): Promise<void>;
  };
  crossTab: {
    broadcastSignedOut(): void;
    onSignedOut(listener: () => void): () => void;
  };
  netInfo: { onOnline(listener: () => void): () => void };
  /** US4: role notices ("Access updated", a role removed, …) go to the app's notice store. */
  notices?: { post(text: string): void };
  /** 001's e2e seed (e2e builds only); wins over a real restore when present. */
  readSeed?: () => E2eSeed | null;
  onSeed?: (seed: E2eSeed) => void;
  clock?: () => number;
  /** Server revoke timeout (research R6). */
  revokeTimeoutMs?: number;
};

export type SignInOutcome =
  { kind: "signedIn"; href: string } | { kind: "refused"; message: string };

export type RenewResult = { notice: string | null };

const REASON_OF: Record<SignOutCause, SignedOutReason> = {
  USER: "SIGNED_OUT",
  SESSION_ENDED: "SESSION_ENDED",
  LINK_PAGE: "LINK_PAGE",
  NO_ACCESS: "NO_ACCESS",
};

const REVOKES: readonly SignOutCause[] = ["USER", "LINK_PAGE", "NO_ACCESS"];

function appError(kind: AppError["kind"], code: string | null): AppError {
  return {
    kind,
    status: null,
    code,
    requestId: null,
    fieldErrors: [],
    retryAfterSeconds: null,
    allowedClients: null,
  };
}

const sessionEnded = (error: AppError): boolean =>
  error.kind === "unauthenticated";

/** FR-026, defensively: the refusal message for a sign-in that produced no served role. */
function refusalFor(auth: AuthSession): string {
  const roles = auth.roles.map((g) => g.role);
  const clients: Api["Client"][] = [];
  if (roles.includes("AKAIVA_ADMIN")) clients.push("admin");
  if (roles.includes("DRIVER")) clients.push("driver");
  return otherClientMessage(clients);
}

export function createSessionController(deps: ControllerDeps) {
  const {
    api,
    platform,
    storage,
    crossTab,
    netInfo,
    revokeTimeoutMs = 5_000,
  } = deps;
  const now = deps.clock ?? Date.now;

  let accessToken: string | null = null;
  let accessTokenExpiresAt: number | null = null;
  let postSignInHref: string | null = null;
  let renewal: Promise<RenewResult> | null = null;

  /** Platform delivery check (R3): web COOKIE, Android BODY with a token. */
  const deliveryMatches = (auth: AuthSession) =>
    platform === "web"
      ? auth.refreshTokenDelivery === "COOKIE"
      : auth.refreshTokenDelivery === "BODY" && !!auth.refreshToken;

  /** Keeps the credentials of a new or renewed session. Android writes the rotated token first. */
  async function keep(auth: AuthSession): Promise<void> {
    if (platform === "android" && auth.refreshToken) {
      await storage.refreshCredential.write(auth.refreshToken);
    }
    if (platform === "web") await storage.sessionHint.set();
    accessToken = auth.accessToken;
    const expiry = Date.parse(auth.accessTokenExpiresAt);
    accessTokenExpiresAt = Number.isNaN(expiry) ? null : expiry;
  }

  function forgetAccess(): void {
    accessToken = null;
    accessTokenExpiresAt = null;
  }

  /** Best-effort server revoke: never awaited by the UI, failures ignored, 5 s timeout. */
  function revoke(refreshToken: string | null): void {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), revokeTimeoutMs);
    api
      .signOut(refreshToken, { signal: abort.signal })
      .catch(() => {})
      .finally(() => clearTimeout(timer));
  }

  /** Local credentials and preferences; the pending join only for the user's own Log out. */
  async function clearLocal(cause: SignOutCause): Promise<void> {
    forgetAccess();
    postSignInHref = null;
    await Promise.all([
      storage.refreshCredential.clear(),
      storage.sessionHint.clear(),
      storage.activeRolePreference.clear(),
      cause === "USER" ? deps.pendingJoin.clear() : Promise.resolve(),
    ]);
  }

  async function storedRefreshToken(): Promise<string | null> {
    return platform === "android" ? storage.refreshCredential.read() : null;
  }

  const controller = {
    /** 001 restorer (FR-020, FR-023, FR-031; P-AUTH-3; R4). */
    async restore(): Promise<SignInPayload | null> {
      const seed = deps.readSeed?.() ?? null;
      if (seed) {
        deps.onSeed?.(seed);
        return seed.session;
      }
      const token = await storedRefreshToken();
      const mayHaveSession =
        platform === "web"
          ? await storage.sessionHint.present()
          : token !== null;
      if (!mayHaveSession) return null;

      let auth: AuthSession;
      try {
        auth = await api.refreshSession(token);
      } catch (error) {
        if (isAppError(error) && sessionEnded(error)) {
          // Credentials and the remembered role go; the pending join stays (FR-022, FR-036).
          await clearLocal("SESSION_ENDED");
          throw { ...error, code: "SESSION_ENDED" } satisfies AppError;
        }
        // Offline, timeout, 5xx, 429: nothing cleared; the store keeps restoring with the error.
        throw error;
      }
      if (!deliveryMatches(auth)) {
        revoke(auth.refreshToken);
        throw appError("server", null);
      }
      const served = servedFromAuth(auth);
      const choice = chooseActiveRole({
        served,
        platform,
        trigger: "restore",
        hasPendingJoin: false,
        remembered: await storage.activeRolePreference.read(),
      });
      if (choice.kind === "refusal") {
        revoke(auth.refreshToken);
        await clearLocal("NO_ACCESS");
        return null;
      }
      await keep(auth);
      return toSignedInSession(auth, choice.activeRole);
    },

    /** 001 credential provider. */
    getAccessToken(): string | null {
      return accessToken;
    },

    /** Epoch ms when the access token stops being accepted (authFetch renews 30 s before). */
    getAccessTokenExpiresAt(): number | null {
      return accessTokenExpiresAt;
    },

    /**
     * Renews the session once for every concurrent caller (FR-021, FR-033; R5). 200: new access
     * token, rotated Android token written before resolving, roles re-applied. 401: signs out with
     * SESSION_ENDED (return address kept by the guard). A connection failure leaves the user signed
     * in and rejects.
     */
    renew(options: { manual?: boolean } = {}): Promise<RenewResult> {
      renewal ??= (async () => {
        try {
          let auth: AuthSession;
          try {
            auth = await api.refreshSession(await storedRefreshToken());
          } catch (error) {
            if (isAppError(error) && sessionEnded(error)) {
              await controller.signOut("SESSION_ENDED");
            }
            throw error;
          }
          if (!deliveryMatches(auth)) throw appError("server", null);
          await keep(auth);

          const state = deps.store().getSnapshot();
          if (state.status !== "signedIn") return { notice: null };
          const applied = applyRoles(
            state,
            servedFromAuth(auth),
            !!options.manual,
          );
          if (applied.kind === "noAccess") {
            await controller.signOut("SESSION_ENDED");
            return { notice: null };
          }
          if (applied.clearPreference)
            await storage.activeRolePreference.clear();
          deps.store().signIn(toSignedInSession(auth, applied.activeRole));
          if (applied.notice) deps.notices?.post(applied.notice);
          return { notice: applied.notice };
        } finally {
          renewal = null;
        }
      })();
      return renewal;
    },

    /**
     * Every sign-in method ends here (FR-002, FR-017 to FR-019, FR-026, FR-034; R-5). The
     * destination is kept before the store changes, so the signed-out layout can read it.
     */
    async completeSignIn(
      auth: AuthSession,
      options: { returnTo?: string | null } = {},
    ): Promise<SignInOutcome> {
      if (!deliveryMatches(auth)) {
        revoke(auth.refreshToken);
        throw appError("server", null);
      }
      const served = servedFromAuth(auth);
      const pending = await deps.pendingJoin.read(now());
      const choice = chooseActiveRole({
        served,
        platform,
        trigger: "signIn",
        hasPendingJoin: pending !== null && served.includes("PARENT"),
        remembered: null,
      });
      if (choice.kind === "refusal") {
        revoke(auth.refreshToken);
        await controller.signOut("NO_ACCESS", { revoke: false });
        return { kind: "refused", message: refusalFor(auth) };
      }

      await keep(auth);
      const session = toSignedInSession(auth, choice.activeRole);
      postSignInHref = resolvePostSignIn({
        session: { status: "signedIn", ...session },
        platform,
        needsRolePick: choice.needsRolePick,
        pendingJoin: pending,
        returnTo: options.returnTo ?? null,
      });
      deps.store().signIn(session);
      return { kind: "signedIn", href: postSignInHref };
    },

    /**
     * Web dual role only (FR-032; R-2): changes the active role without signing in again and
     * remembers it for the next restore. Android never switches or writes the preference (R-4).
     */
    async switchRole(role: ServedRole): Promise<void> {
      if (platform !== "web") return;
      const state = deps.store().getSnapshot();
      if (state.status !== "signedIn") return;
      const served = servedRoles(state.roles);
      if (!served.includes("PARENT") || !served.includes("SCHOOL_ADMIN"))
        return;
      if (!served.includes(role)) return;
      deps.store().setActiveRole(role);
      await storage.activeRolePreference.write(role);
    },

    /**
     * Refresh access (FR-033; R-8): renews at once and re-applies the roles with a notice. An ended
     * session signs out (FR-022, inside renew); a connection failure leaves everything as it was.
     */
    async refreshAccess(): Promise<void> {
      await controller.renew({ manual: true }).catch(() => {});
    },

    /**
     * A protected call was refused for the role or school (FR-046; R-7, R-8): renew (shared with
     * any renewal under way) so the landing follows the roles the server now reports.
     */
    async onForbidden(): Promise<void> {
      await controller.renew().catch(() => {});
    },

    /** The destination chosen at sign-in, without consuming it (render-safe). */
    peekPostSignInHref(): string | null {
      return postSignInHref;
    },

    /** The destination chosen at sign-in, once. */
    takePostSignInHref(): string | null {
      const href = postSignInHref;
      postSignInHref = null;
      return href;
    },

    /**
     * Signs out (FR-022, FR-024, FR-041; research R6). Local state is cleared first, so it works
     * offline; the server revoke for USER, LINK_PAGE and NO_ACCESS is fire-and-forget.
     */
    async signOut(
      cause: SignOutCause,
      options: { revoke?: boolean } = {},
    ): Promise<void> {
      const refreshToken = await storedRefreshToken();
      const done = deps.store().signOut(REASON_OF[cause], cause);
      if (options.revoke !== false && REVOKES.includes(cause))
        revoke(refreshToken);
      if (cause === "USER") crossTab.broadcastSignedOut();
      await done;
    },

    /** The personal-data cleaner the store runs on every sign-out. */
    clearLocal,

    /** Starts the listeners: another tab logged out; the connection came back during a restore. */
    start(): () => void {
      const stopTabs = crossTab.onSignedOut(() => {
        if (deps.store().getSnapshot().status !== "signedOut") {
          void deps.store().signOut("SIGNED_OUT", "USER");
        }
      });
      const stopOnline = netInfo.onOnline(() => {
        const state = deps.store().getSnapshot();
        if (state.status === "restoring" && state.error)
          void deps.store().retryRestore();
      });
      return () => {
        stopTabs();
        stopOnline();
      };
    },
  };

  return controller;
}

export type SessionController = ReturnType<typeof createSessionController>;
